import {
  EXPOSED_RESPONSE_HEADERS,
  SAFE_METHODS,
  type HttpMethod,
  type ResponseHeaders,
  type TransportErrorDetails,
  type QueryValue,
  type RequestObserver,
  type TransportError,
  type TransportErrorKind,
  type TransportRequest,
  type TransportResult,
} from './transport'
import { ANTIFORGERY_FAILED, GATEWAY_URLS, TRANSPORT_HEADERS } from './urls'

/**
 * Единственное место нового клиента, где вызывается `fetch` (D-053, eslint).
 *
 * Что здесь решено один раз для всех пакетов:
 *
 * - **Cookie, а не токен.** `credentials: 'same-origin'`: сессию несут
 *   HttpOnly-cookie Gateway/BFF, JavaScript токенов не видит и не хранит (D-084).
 * - **CSRF.** Для изменяющих методов — заголовок `X-CSRF-TOKEN`. Токен живёт
 *   только в памяти этого клиента и берётся у Gateway по первому изменяющему
 *   запросу (D-083).
 * - **Корреляция.** Каждый запрос получает `X-Correlation-Id`; Gateway его
 *   нормализует, служба пишет в журнал, экран — в отчёт об ошибке.
 * - **Повторы.** Безопасное чтение повторяется при сбое сети и 502/503/504.
 *   Изменяющий запрос не повторяется НИКОГДА, кроме одного случая: Gateway
 *   отверг его антифоргери-проверкой (400 `ANTIFORGERY_FAILED`) — такой запрос
 *   до службы не дошёл, и повтор с новым токеном не может создать второй
 *   документ. 401 на изменяющий запрос не повторяется (D-085): сессию продлевает
 *   Gateway сам, а неизвестный исход команды проверяется чтением, не повтором.
 *   Чтение с `noRetry` тоже не повторяется. Команда с признаком `idempotent` (ключ идемпотентности и версия) тоже не повторяется
 *   самим transport: исход без ответа и 5xx помечаются `retryable: true`, а расписание повторов тем же ключом и тем же телом ведёт
 *   вызывающий (очередь команд корзины и оплаты — видимые «попытка N» и «Отменить»).
 * - **Неизвестный исход.** Изменяющий запрос без ответа (сеть, таймаут, обрыв
 *   тела) и 502/503/504 без кода причины в теле — `unknown-outcome`: прокси мог
 *   не дождаться службы, уже исполнившей команду. 5xx с кодом (`access-unavailable`,
 *   `onec-unavailable`) — `unavailable`: причину назвал сервер.
 * - **Тело.** JSON (`body`) или multipart (`formData`, `Content-Type` с boundary
 *   ставит браузер). Ответ — JSON, текст или Blob (`responseType`).
 */

export interface HttpClientOptions {
  /** Реализация fetch. В приложении — `globalThis.fetch`, в тестах — подставная. */
  fetch: typeof fetch
  /**
   * Создатель `XMLHttpRequest` - только для запросов с наблюдателем (`request(..., observer)`): прогресс передачи файла и построчный
   * ответ. Не задан - такие запросы идут через `fetch` без прогресса. В приложении - `() => new XMLHttpRequest()`.
   */
  xhr?: () => XMLHttpRequest
  /** Таймаут по умолчанию, мс. */
  defaultTimeoutMs?: number
  /** Сколько раз повторить безопасное чтение после сбоя сети/502/503/504. */
  safeRetries?: number
  /** Пауза перед повтором, мс; растёт вдвое. */
  retryDelayMs?: number
  /** Источник корреляции. В тестах — детерминированный. */
  newCorrelationId?: () => string
  /** Вызывается на 401: общий механизм входа (D-085). Повтора не происходит. */
  onUnauthorized?: (request: { url: string; method: HttpMethod }) => void
  /**
   * Сервер приложил к ответу идентификатор сборки клиента (`X-Client-Build`). Вызывается на каждом ответе, где он есть:
   * сравнить со своей сборкой и показать «вышла новая версия» — дело приложения, опроса сервера для этого нет.
   */
  onServerBuild?: (buildId: string) => void
  /**
   * Итог запроса для общей системы связи с сервером (решение владельца 23.10.2026): вызывается после ФИНАЛЬНОГО исхода,
   * когда собственные повторы чтения уже исчерпаны. `ok: true` — сервер ответил (любой HTTP-ответ, включая 4xx и 5xx с
   * кодом причины); `ok: false` — связи нет: сеть, таймаут, изменяющий запрос без ответа, 502/503/504 прокси без кода
   * причины. Отмена запроса самим клиентом (`aborted`) о связи ничего не говорит и не сообщается.
   */
  onConnection?: (event: ConnectionEvent) => void
  /**
   * Началось действие человека — изменяющий запрос не из служебных (`background`) и не `readOnly`; возвращённая функция
   * вызывается по его окончании: `reached` — сервер ответил. Окно связи показывается, только когда человек ждёт ответа.
   */
  onUserAction?: () => (reached: boolean) => void
  /**
   * Хронометраж каждого запроса для подробной трассировки продажи (RP-SALE-TRACE): вызывается после финального исхода,
   * ничего не меняет в запросе, а сбой обработчика глотается. Адрес без параметров запроса — они могут нести персональные данные.
   */
  onRequestTimed?: (timing: RequestTiming) => void
  /**
   * Заголовки, которые уходят с каждым запросом: устройство и вкладка клиента (RP-CA4). Заголовки самого запроса и транспорта
   * (корреляция, CSRF, подтверждение кассира) их не перекрывают.
   */
  identityHeaders?: () => Record<string, string>
}

/** Хронометраж одного запроса (после всех повторов). */
export interface RequestTiming {
  method: HttpMethod
  /** Путь запроса без параметров и фрагмента. */
  path: string
  /** Начало запроса, мс от эпохи. */
  startedAt: number
  durationMs: number
  /** HTTP-статус ответа; `null` — ответа не было (сеть, таймаут, отмена). */
  status: number | null
  /** Сколько раз запрос уходил в сеть (повтор чтения — вторая попытка). */
  attempts: number
  /** Размер тела ответа по `Content-Length`; `null` — заголовка нет. */
  bytes: number | null
  /** Служебный фоновый запрос (трассировка и т. п.). */
  background: boolean
}

/** Что запрос сказал о связи с сервером. */
export type ConnectionEvent = { ok: true } | { ok: false; error: TransportError }

export interface HttpClient {
  request<T = unknown>(request: TransportRequest, signal?: AbortSignal, observer?: RequestObserver): Promise<TransportResult<T>>
  /** Забыть токен антифоргери: после выхода и смены пользователя он чужой. */
  resetCsrf(): void
}

const DEFAULT_TIMEOUT_MS = 30_000
const DEFAULT_SAFE_RETRIES = 2
const DEFAULT_RETRY_DELAY_MS = 400
const RETRYABLE_STATUSES = new Set([502, 503, 504])

/**
 * Связь с сервером по итогу запроса; `null` — запрос отменён клиентом и о связи не говорит. 502/503/504 без кода причины
 * (`code` остался видом ошибки) отдал прокси, а не наша служба: это сбой связи. 5xx с кодом назвал сервер — он доступен.
 */
export function connectionEventOf(result: TransportResult<unknown>): ConnectionEvent | null {
  if (result.ok) return { ok: true }
  const { error } = result
  if (error.kind === 'network' && error.code === 'aborted') return null
  if (error.kind === 'network' || error.kind === 'timeout' || error.kind === 'unknown-outcome') return { ok: false, error }
  if (error.kind === 'unavailable' && error.code === 'unavailable' && error.status !== undefined && RETRYABLE_STATUSES.has(error.status)) {
    return { ok: false, error }
  }
  return { ok: true }
}

/** Что `perform` сообщает наблюдателю времени: число попыток и последний ответ. */
interface AttemptStats {
  attempts: number
  status: number | null
  bytes: number | null
}

function pathOnly(url: string): string {
  const end = url.search(/[?#]/)
  return end < 0 ? url : url.slice(0, end)
}

function contentLengthOf(response: Response): number | null {
  const raw = response.headers.get('content-length')
  if (raw === null) return null
  const value = Number(raw)
  return Number.isSafeInteger(value) && value >= 0 ? value : null
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * UUID корреляции и намерений человека — единственный генератор клиента.
 * `crypto.randomUUID` есть только в secure context (https, localhost); dev-сервер
 * и касса по адресу в локальной сети — не он, поэтому запасной путь через
 * `getRandomValues`.
 */
export function newUuid(): string {
  const crypto = globalThis.crypto
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * Сигнал, который срабатывает от любого из двух. `AbortSignal.any` есть не во
 * всех поддерживаемых браузерах (Safari < 17.4, старые WebView касс), поэтому
 * запасной путь — подписка.
 */
function eitherSignal(first: AbortSignal | undefined, second: AbortController): AbortSignal {
  if (first === undefined) return second.signal
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([first, second.signal])
  if (first.aborted) second.abort()
  else first.addEventListener('abort', () => second.abort(), { once: true })
  return second.signal
}

/** Таймаут запроса за токеном антифоргери, мс. */
const CSRF_TIMEOUT_MS = 10_000

/** Адрес только на своём origin: внешний URL с cookie сессии — утечка. */
function assertSameOrigin(url: string): void {
  if (!url.startsWith('/') || url.startsWith('//')) {
    throw new Error(`Transport принимает только путь на своём origin, получено: ${url}`)
  }
}

function buildUrl(url: string, params: TransportRequest['params']): string {
  if (params === undefined) return url
  const search = new URLSearchParams()
  const append = (name: string, value: QueryValue) => {
    if (value === undefined || value === null) return
    search.append(name, String(value))
  }
  for (const [name, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value as readonly QueryValue[]) append(name, item)
    } else {
      append(name, value as QueryValue)
    }
  }
  const query = search.toString()
  if (query === '') return url
  return url.includes('?') ? `${url}&${query}` : `${url}?${query}`
}

function assertSingleBody(request: TransportRequest): void {
  if (request.body !== undefined && request.formData !== undefined) {
    throw new Error('Transport: у запроса одно тело — body (JSON) или formData (multipart), не оба')
  }
}

const EXPOSED = new Set(EXPOSED_RESPONSE_HEADERS)

/** Заголовки ответа из закрытого списка, имена в нижнем регистре. */
function exposedHeaders(response: Response): ResponseHeaders {
  const headers: Record<string, string> = {}
  response.headers.forEach((value, name) => {
    const key = name.toLowerCase()
    if (EXPOSED.has(key)) headers[key] = value
  })
  return headers
}

/** Тело ошибки сервера в любом из встречающихся видов: `{code,message}`, ProblemDetails. */
interface ErrorBody {
  code?: unknown
  message?: unknown
  detail?: unknown
  title?: unknown
  errors?: unknown
}

/**
 * Поля тела отказа, которые уже стали полями `TransportError` (`code`,
 * `message`, `errors` → `fields`), и служебные поля ProblemDetails.
 */
const NOT_DETAILS = new Set(['code', 'message', 'errors', 'type', 'title', 'status', 'detail', 'instance', 'traceId'])

function readDetails(body: object): TransportErrorDetails | undefined {
  const details: Record<string, unknown> = {}
  for (const [name, value] of Object.entries(body)) {
    if (!NOT_DETAILS.has(name) && value !== undefined) details[name] = value
  }
  return Object.keys(details).length > 0 ? details : undefined
}

function readFields(errors: unknown): Record<string, readonly string[]> | undefined {
  if (typeof errors !== 'object' || errors === null || Array.isArray(errors)) return undefined
  const fields: Record<string, readonly string[]> = {}
  for (const [name, value] of Object.entries(errors)) {
    if (Array.isArray(value)) fields[name] = value.filter((item): item is string => typeof item === 'string')
  }
  return Object.keys(fields).length > 0 ? fields : undefined
}

const DEFAULT_MESSAGES: Record<TransportErrorKind, string> = {
  unauthorized: 'Сессия завершена — войдите снова',
  forbidden: 'Нет доступа к этому действию',
  csrf: 'Обновите страницу: истёк защитный токен',
  'not-found': 'Не найдено',
  conflict: 'Данные изменились — перечитайте и повторите',
  validation: 'Запрос не прошёл проверку',
  unavailable: 'Служба временно недоступна',
  http: 'Сервер вернул ошибку',
  network: 'Нет связи с сервером',
  timeout: 'Сервер не ответил вовремя',
  'unknown-outcome': 'Ответ не получен: неизвестно, выполнена ли операция. Проверьте состояние, прежде чем повторять',
  'invalid-response': 'Сервер прислал ответ в неожиданном виде',
}

function kindOfStatus(status: number, code: string | undefined): TransportErrorKind {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not-found'
  if (status === 409 || status === 412) return 'conflict'
  if (status === 400 && code === ANTIFORGERY_FAILED) return 'csrf'
  if (status === 400 || status === 422) return 'validation'
  if (RETRYABLE_STATUSES.has(status)) return 'unavailable'
  return 'http'
}

/**
 * Текст отказа, когда сервер не прислал своего. 413 — самый частый случай загрузки файла: запрос больше, чем принимает прокси
 * или шлюз. Остальные коды без JSON получают статус в тексте, чтобы его можно было передать администратору.
 */
function plainStatusMessage(status: number, kind: TransportErrorKind, bodyWasJson: boolean): string | undefined {
  if (status === 413) return 'Файл больше, чем принимает сервер (HTTP 413). Уменьшите снимок или сообщите администратору: лимит задаёт входной прокси'
  if (kind === 'http' || kind === 'validation') {
    return bodyWasJson ? undefined : `${DEFAULT_MESSAGES[kind]} (HTTP ${status}), пояснения в ответе нет`
  }
  return undefined
}

function errorOf(
  kind: TransportErrorKind,
  correlationId: string,
  options: {
    status?: number
    code?: string
    message?: string
    fields?: Record<string, readonly string[]>
    details?: TransportErrorDetails
    retryable?: boolean
  } = {},
): TransportError {
  return {
    kind,
    ...(options.status !== undefined ? { status: options.status } : {}),
    code: options.code ?? kind,
    message: options.message ?? DEFAULT_MESSAGES[kind],
    correlationId,
    retryable: options.retryable ?? false,
    ...(options.fields !== undefined ? { fields: options.fields } : {}),
    ...(options.details !== undefined ? { details: options.details } : {}),
  }
}

/** Успешный ответ в заказанном виде; ответ отказа — всегда как JSON. */
async function readBody(
  response: Response,
  responseType: TransportRequest['responseType'],
): Promise<{ ok: true; value: unknown } | { ok: false }> {
  if (!response.ok || responseType === undefined || responseType === 'json') return readJson(response)
  if (responseType === 'text') return { ok: true, value: await response.text() }
  if (responseType === 'ndjson') return parseNdjson(await response.text())
  return { ok: true, value: await response.blob() }
}

/** Строки NDJSON -> массив значений; пустые строки пропускаются, битая строка - ответ непригоден. */
function parseNdjson(text: string): { ok: true; value: unknown[] } | { ok: false } {
  const values: unknown[] = []
  for (const line of text.split('\n')) {
    if (line.trim() === '') continue
    try {
      values.push(JSON.parse(line) as unknown)
    } catch {
      return { ok: false }
    }
  }
  return { ok: true, value: values }
}

async function readJson(response: Response): Promise<{ ok: true; value: unknown } | { ok: false }> {
  if (response.status === 204 || response.status === 205) return { ok: true, value: null }
  const text = await response.text()
  if (text.trim() === '') return { ok: true, value: null }
  try {
    return { ok: true, value: JSON.parse(text) as unknown }
  } catch {
    return { ok: false }
  }
}

/** Статусы, при которых у ответа не бывает тела: `new Response(text, { status })` для них бросает. */
const BODYLESS = new Set([101, 204, 205, 304])

type AttemptResult =
  | { response: Response; parsed: { ok: true; value: unknown } | { ok: false } }
  | { failure: 'network' | 'timeout' | 'aborted' }

/**
 * Одна попытка через `XMLHttpRequest`: тот же результат, что у `fetch`-попытки, плюс прогресс передачи тела и строки NDJSON по мере
 * прихода. Таймаут охватывает весь запрос, включая передачу и чтение.
 */
function attemptXhr(
  create: () => XMLHttpRequest,
  request: TransportRequest,
  method: HttpMethod,
  url: string,
  headers: Record<string, string>,
  signal: AbortSignal | undefined,
  observer: RequestObserver,
  timeoutMs: number,
): Promise<AttemptResult> {
  return new Promise((resolve) => {
    if (signal?.aborted === true) {
      resolve({ failure: 'aborted' })
      return
    }
    const xhr = create()
    xhr.open(method, url)
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value)
    xhr.responseType = 'text'
    xhr.timeout = timeoutMs
    let delivered = 0
    const deliver = (final: boolean) => {
      if (observer.onLine === undefined || request.responseType !== 'ndjson' || xhr.status < 200 || xhr.status > 299) return
      const text = xhr.responseText
      const end = final ? text.length : text.lastIndexOf('\n') + 1
      if (end <= delivered) return
      for (const line of text.slice(delivered, end).split('\n')) {
        if (line.trim() === '') continue
        try {
          observer.onLine(JSON.parse(line) as unknown)
        } catch {
          // Битая строка станет ошибкой разбора ответа ниже; наблюдателю её не отдаём.
        }
      }
      delivered = end
    }
    const onAbort = () => xhr.abort()
    signal?.addEventListener('abort', onAbort, { once: true })
    const finish = (result: AttemptResult) => {
      signal?.removeEventListener('abort', onAbort)
      resolve(result)
    }
    xhr.upload.onprogress = (event) => observer.onUploadProgress?.(event.loaded, event.lengthComputable ? event.total : null)
    xhr.onprogress = () => deliver(false)
    xhr.onerror = () => finish({ failure: 'network' })
    xhr.ontimeout = () => finish({ failure: 'timeout' })
    xhr.onabort = () => finish({ failure: signal?.aborted === true ? 'aborted' : 'network' })
    xhr.onload = () => {
      deliver(true)
      const responseHeaders = new Headers()
      for (const line of xhr.getAllResponseHeaders().split(/\r?\n/)) {
        const colon = line.indexOf(':')
        if (colon > 0) responseHeaders.append(line.slice(0, colon).trim(), line.slice(colon + 1).trim())
      }
      const response = new Response(BODYLESS.has(xhr.status) ? null : xhr.responseText, { status: xhr.status, headers: responseHeaders })
      void readBody(response.clone(), request.responseType).then((parsed) => finish({ response, parsed }))
    }
    if (request.formData !== undefined) xhr.send(request.formData)
    else xhr.send(request.body !== undefined ? JSON.stringify(request.body) : null)
  })
}

export function createHttpClient(options: HttpClientOptions): HttpClient {
  const fetchImpl = options.fetch
  const defaultTimeoutMs = options.defaultTimeoutMs ?? DEFAULT_TIMEOUT_MS
  const safeRetries = options.safeRetries ?? DEFAULT_SAFE_RETRIES
  const retryDelayMs = options.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS
  const newCorrelationId = options.newCorrelationId ?? newUuid

  /** Токен антифоргери — только в памяти (D-083), не в Redux и не в storage. */
  let csrfToken: string | null = null
  /** Полёт запроса за токеном: параллельные POST не заказывают его трижды. */
  let csrfInFlight: Promise<void> | null = null

  async function fetchCsrfToken(): Promise<void> {
    const timeout = new AbortController()
    const timer = setTimeout(() => timeout.abort(), CSRF_TIMEOUT_MS)
    try {
      const response = await fetchImpl(GATEWAY_URLS.antiforgery, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', [TRANSPORT_HEADERS.correlation]: newCorrelationId() },
        signal: timeout.signal,
      })
      if (!response.ok) throw new Error(`antiforgery ${response.status}`)
      const body = (await response.json()) as { token?: unknown } | null
      if (typeof body?.token !== 'string' || body.token === '') throw new Error('antiforgery: нет token')
      csrfToken = body.token
    } finally {
      clearTimeout(timer)
    }
  }

  async function ensureCsrfToken(): Promise<void> {
    if (csrfToken !== null) return
    csrfInFlight ??= fetchCsrfToken().finally(() => {
      csrfInFlight = null
    })
    await csrfInFlight
  }

  /**
   * Одна попытка запроса: ответ ЦЕЛИКОМ (заголовки и тело), сетевой сбой или
   * таймаут. Тело читается под тем же таймаутом и в том же `try`: обрыв на
   * чтении тела 2xx-ответа команды — тоже неизвестный исход, а не «ошибка клиента».
   */
  async function attempt(
    request: TransportRequest,
    method: HttpMethod,
    url: string,
    correlationId: string,
    signal: AbortSignal | undefined,
    observer: RequestObserver | undefined,
  ): Promise<
    | { response: Response; parsed: { ok: true; value: unknown } | { ok: false } }
    | { failure: 'network' | 'timeout' | 'aborted' }
  > {
    const unsafe = !SAFE_METHODS.has(method)
    const responseType = request.responseType ?? 'json'
    const headers: Record<string, string> = {
      ...(options.identityHeaders?.() ?? {}),
      // Отказ сервер отдаёт JSON и при файловом ответе, поэтому JSON в списке всегда.
      Accept: responseType === 'json' ? 'application/json' : 'application/json;q=0.9, */*;q=0.8',
      [TRANSPORT_HEADERS.correlation]: correlationId,
    }
    // У multipart Content-Type не ставится: boundary знает только браузер.
    if (request.body !== undefined) headers['Content-Type'] = 'application/json'
    if (request.idempotencyKey !== undefined) headers[TRANSPORT_HEADERS.idempotency] = request.idempotencyKey
    if (request.cashierGrant !== undefined) headers['X-Cashier-Grant'] = request.cashierGrant
    if (unsafe) {
      try {
        await ensureCsrfToken()
      } catch {
        // Токен не получен — запрос уходит без него: Gateway ответит
        // ANTIFORGERY_FAILED, и это честнее, чем молча проглотить команду.
      }
      if (csrfToken !== null) headers[TRANSPORT_HEADERS.csrf] = csrfToken
    }

    if (observer !== undefined && options.xhr !== undefined) {
      return attemptXhr(options.xhr, request, method, url, headers, signal, observer, request.timeoutMs ?? defaultTimeoutMs)
    }

    const timeout = new AbortController()
    const timer = setTimeout(() => timeout.abort(), request.timeoutMs ?? defaultTimeoutMs)
    try {
      const combined = eitherSignal(signal, timeout)
      const response = await fetchImpl(url, {
        method,
        credentials: 'same-origin',
        headers,
        ...(request.formData !== undefined
          ? { body: request.formData }
          : request.body !== undefined
            ? { body: JSON.stringify(request.body) }
            : {}),
        signal: combined,
      })
      const parsed = await readBody(response, request.responseType)
      if (parsed.ok && response.ok && Array.isArray(parsed.value)) for (const line of parsed.value) observer?.onLine?.(line)
      return { response, parsed }
    } catch {
      if (signal?.aborted === true) return { failure: 'aborted' }
      return { failure: timeout.signal.aborted ? 'timeout' : 'network' }
    } finally {
      clearTimeout(timer)
    }
  }

  /** Запрос с сообщением о связи: сигнал — после всех повторов, по итоговому результату. */
  async function request<T>(
    request: TransportRequest,
    signal?: AbortSignal,
    observer?: RequestObserver,
  ): Promise<TransportResult<T>> {
    const startedAt = Date.now()
    const stats: AttemptStats = { attempts: 0, status: null, bytes: null }
    const userAction =
      !SAFE_METHODS.has(request.method ?? 'GET') && request.readOnly !== true && request.background !== true
        ? options.onUserAction?.()
        : undefined
    const result = await perform<T>(request, signal, observer, stats)
    if (userAction !== undefined) {
      const event = connectionEventOf(result)
      userAction(event === null || event.ok)
    }
    if (options.onRequestTimed !== undefined) {
      try {
        options.onRequestTimed({
          method: request.method ?? 'GET',
          path: pathOnly(request.url),
          startedAt,
          durationMs: Math.max(0, Date.now() - startedAt),
          status: stats.status,
          attempts: stats.attempts,
          bytes: stats.bytes,
          background: request.background === true,
        })
      } catch {
        // Наблюдение за временем не вправе влиять на запрос.
      }
    }
    // Служебный запрос (`background`) о связи не говорит: его сбой не должен показываться кассиру как «нет связи».
    if (options.onConnection !== undefined && request.background !== true) {
      const event = connectionEventOf(result)
      if (event !== null) options.onConnection(event)
    }
    return result
  }

  async function perform<T>(
    request: TransportRequest,
    signal: AbortSignal | undefined,
    observer: RequestObserver | undefined,
    stats: AttemptStats,
  ): Promise<TransportResult<T>> {
    assertSameOrigin(request.url)
    assertSingleBody(request)
    const method = request.method ?? 'GET'
    const safe = SAFE_METHODS.has(method) || request.readOnly === true
    /** Сколько повторов допустимо этому запросу: изменяющему и `noRetry` — ни одного. */
    const retries = safe && request.noRetry !== true ? safeRetries : 0
    const url = buildUrl(request.url, request.params)
    let correlationId = newCorrelationId()
    let csrfRetried = false

    for (let attemptNo = 0; ; attemptNo += 1) {
      stats.attempts = attemptNo + 1
      stats.status = null
      stats.bytes = null
      const outcome = await attempt(request, method, url, correlationId, signal, observer)

      if ('failure' in outcome) {
        if (outcome.failure === 'aborted') {
          return { ok: false, error: errorOf('network', correlationId, { code: 'aborted', message: 'Запрос отменён' }) }
        }
        if (!safe) {
          // Команда могла дойти до службы: исход неизвестен, повтор запрещён (кроме идемпотентной: её повторяет вызывающий тем же ключом).
          return { ok: false, error: errorOf('unknown-outcome', correlationId, { retryable: request.idempotent === true }) }
        }
        if (attemptNo < retries) {
          await delay(retryDelayMs * 2 ** attemptNo)
          correlationId = newCorrelationId()
          continue
        }
        return { ok: false, error: errorOf(outcome.failure, correlationId, { retryable: true }) }
      }

      const { response, parsed } = outcome
      stats.status = response.status
      stats.bytes = contentLengthOf(response)
      // Шлюз отдаёт заголовок дважды (свой и службы, значения равны) — fetch склеивает их через запятую (RP-U9).
      const echoed = response.headers.get(TRANSPORT_HEADERS.correlation)?.split(',')[0]?.trim()
      if (echoed !== undefined && echoed !== '') correlationId = echoed
      const serverBuild = response.headers.get(TRANSPORT_HEADERS.clientBuild)?.trim()
      if (serverBuild !== undefined && serverBuild !== '') options.onServerBuild?.(serverBuild)

      if (response.ok) {
        if (!parsed.ok) {
          // Успешный статус, но не JSON (страница-заглушка прокси, обрезанный ответ): показываем статус, а не просто «неожиданный вид».
          return {
            ok: false,
            error: errorOf('invalid-response', correlationId, {
              status: response.status,
              message: `${DEFAULT_MESSAGES['invalid-response']}: HTTP ${response.status}, тело не JSON`,
            }),
          }
        }
        const headers = exposedHeaders(response)
        if (request.decode === undefined) {
          return { ok: true, data: parsed.value as T, status: response.status, correlationId, headers }
        }
        try {
          return { ok: true, data: request.decode(parsed.value) as T, status: response.status, correlationId, headers }
        } catch (failure) {
          const detail = failure instanceof Error ? failure.message : String(failure)
          return {
            ok: false,
            error: errorOf('invalid-response', correlationId, {
              status: response.status,
              message: `${DEFAULT_MESSAGES['invalid-response']}: ${detail}`,
            }),
          }
        }
      }

      const body: ErrorBody =
        parsed.ok && typeof parsed.value === 'object' && parsed.value !== null && !Array.isArray(parsed.value)
          ? parsed.value
          : {}
      const code = typeof body.code === 'string' ? body.code : undefined

      if (response.status === 400 && code === ANTIFORGERY_FAILED && !csrfRetried) {
        // Отказ Gateway ДО службы: команда не выполнялась, повтор с новым токеном безопасен.
        csrfRetried = true
        csrfToken = null
        correlationId = newCorrelationId()
        continue
      }

      if (!safe && RETRYABLE_STATUSES.has(response.status) && code === undefined) {
        // Прокси не дождался службы или потерял её ответ: команда могла исполниться.
        return {
          ok: false,
          error: errorOf('unknown-outcome', correlationId, { status: response.status, retryable: request.idempotent === true }),
        }
      }

      if (RETRYABLE_STATUSES.has(response.status) && attemptNo < retries) {
        await delay(retryDelayMs * 2 ** attemptNo)
        correlationId = newCorrelationId()
        continue
      }

      const kind = kindOfStatus(response.status, code)
      if (kind === 'unauthorized') options.onUnauthorized?.({ url: request.url, method })
      const jsonMessage = [body.message, body.detail, body.title].find(
        (value): value is string => typeof value === 'string' && value.trim() !== '',
      )
      // Прокси или шлюз ответили не JSON-ом (HTML 413/502, пустое тело): без пояснения человек видел бы «Сервер вернул ошибку».
      const message = jsonMessage ?? plainStatusMessage(response.status, kind, parsed.ok && parsed.value !== null)
      const fields = readFields(body.errors)
      const details = readDetails(body)
      return {
        ok: false,
        error: errorOf(kind, correlationId, {
          status: response.status,
          ...(code !== undefined ? { code } : response.status === 413 ? { code: 'payload-too-large' } : {}),
          ...(message !== undefined ? { message } : {}),
          ...(fields !== undefined ? { fields } : {}),
          ...(details !== undefined ? { details } : {}),
          retryable: (safe && kind === 'unavailable') || (request.idempotent === true && response.status >= 500),
        }),
      }
    }
  }

  return {
    request,
    resetCsrf() {
      csrfToken = null
    },
  }
}
